import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  aiChatMessage,
  aiDiagnostics,
  aiStartSession,
  getAiSession,
  listAiSessions,
} from '../../services/purchaseProgramApi';

// ─── API key / setup error detection ─────────────────────────────────────────
// Backend raises a ValueError mentioning "OpenAI API key" or "openaiKey" when
// the global Customer Identity setting hasn't been populated. Surface that
// case as a dedicated, actionable callout instead of a generic toast.
const CI_SETTINGS_PATH = '/customer-identity/settings';

function isOpenAIKeyMissingError(message) {
  if (!message) return false;
  const m = String(message).toLowerCase();
  return (
    m.includes('openai api key') ||
    m.includes('openaikey') ||
    m.includes('api key not configured')
  );
}

// ─── Diagnostics panel ───────────────────────────────────────────────────────

function DiagnosticsRow({ label, value, hint, ok }) {
  const color = ok === true ? '#15803d' : ok === false ? '#b91c1c' : '#374151';
  const icon = ok === true ? 'bi-check-circle-fill' : ok === false ? 'bi-x-circle-fill' : 'bi-info-circle';
  return (
    <div style={{ display: 'flex', gap: 8, padding: '4px 0', alignItems: 'flex-start' }}>
      <i className={`bi ${icon}`} style={{ color, marginTop: 2 }} aria-hidden="true" />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12, color: '#374151' }}>
          <strong>{label}:</strong> <span style={{ color }}>{value}</span>
        </div>
        {hint && <div style={{ fontSize: 11, color: '#6b7280', marginTop: 1 }}>{hint}</div>}
      </div>
    </div>
  );
}

function DiagnosticsPanel({ data, onClose }) {
  if (data?.error) {
    return (
      <div style={{ marginTop: 10, background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 12px', fontSize: 12, color: '#b91c1c' }}>
        <strong>Diagnostic failed:</strong> {data.error}
        <button type="button" onClick={onClose} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer', color: '#b91c1c' }} aria-label="Close diagnostics">
          <i className="bi bi-x-lg" aria-hidden="true" />
        </button>
      </div>
    );
  }
  const def = data?.definition || {};
  const val = data?.global_value || {};
  const source = data?.resolved_source || 'none';
  const sourceLabel = {
    ci_setting: `Customer Identity → Settings (${val.setting_key || 'openaiKey'}, global)`,
    env: 'OPENAI_API_KEY environment variable',
    legacy: 'Legacy configurations row',
    none: 'NOT FOUND — AI cannot start',
  }[source] || source;

  return (
    <div style={{ marginTop: 10, background: '#fff', border: '1px solid #fbbf24', borderRadius: 8, padding: '10px 12px', fontSize: 12, color: '#374151' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <strong>Diagnostic result</strong>
        <button type="button" onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#92400e' }} aria-label="Close diagnostics">
          <i className="bi bi-x-lg" aria-hidden="true" />
        </button>
      </div>
      <DiagnosticsRow
        label="Resolved account ID"
        value={data?.account_id ?? 'unknown'}
        hint="Make sure this matches the account where you saved the openaiKey value."
      />
      <DiagnosticsRow
        label="Definition exists"
        ok={def.exists}
        value={def.exists ? `yes (key: ${def.setting_key}${def.is_active === false ? ', inactive' : ''})` : 'no'}
        hint={!def.exists ? 'Restart the backend or create a setting definition with key "openaiKey".' : null}
      />
      <DiagnosticsRow
        label="Global value saved"
        ok={val.exists && !val.is_empty}
        value={val.exists ? (val.is_empty ? 'present but empty' : `yes (length: ${val.length})`) : 'no'}
        hint={
          !val.exists
            ? 'Open Customer Identity → Settings → Manage values, choose Global scope, save your key.'
            : val.is_empty
              ? 'A row exists but the value decoded to empty. Re-save the key.'
              : null
        }
      />
      <DiagnosticsRow
        label="OPENAI_API_KEY env var"
        ok={data?.env_var_set || undefined}
        value={data?.env_var_set ? 'set' : 'not set'}
      />
      <DiagnosticsRow
        label="Legacy configurations row"
        value={data?.legacy_configurations_exists ? 'present' : 'absent'}
      />
      <div style={{ marginTop: 6, padding: '6px 8px', borderRadius: 6, background: source === 'none' ? '#fee2e2' : '#dcfce7', color: source === 'none' ? '#991b1b' : '#166534' }}>
        <strong>Resolved source:</strong> {sourceLabel}
        {data?.resolved_length > 0 && <> · key length: {data.resolved_length}</>}
      </div>
    </div>
  );
}

// ─── Message bubble ───────────────────────────────────────────────────────────

function formatMessageTime(ts) {
  if (!ts) return '';
  const date = new Date(ts);
  if (!Number.isNaN(date.getTime())) {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  const utcDate = new Date(`${ts}Z`);
  if (!Number.isNaN(utcDate.getTime())) {
    return utcDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  return '';
}

function nullButtonLabel(field) {
  if (field === 'start_date') return 'Start immediately (no fixed date)';
  if (field === 'end_date') return 'No end date (ongoing)';
  if (field === 'period_anchor_date') return 'No anchor date';
  return 'Skip (use null)';
}

function DateInputBlock({ inputHint, onPick, disabled = false }) {
  const [local, setLocal] = useState('');
  if (!inputHint || inputHint.type !== 'date') return null;
  const field = inputHint.field || 'start_date';
  const nullable = inputHint.nullable !== false;

  return (
    <div
      className="mt-2 pt-2 border-top border-light"
      style={{ borderStyle: 'dashed' }}
    >
      <div className="small fw-semibold text-secondary mb-2">
        <i className="bi bi-calendar3 me-1" aria-hidden="true" />
        Choose a date (optional)
      </div>
      <div className="d-flex flex-column flex-sm-row flex-wrap gap-2 align-items-stretch align-items-sm-center">
        <input
          type="date"
          className="form-control form-control-sm"
          style={{ maxWidth: '100%', width: 200 }}
          value={local}
          onChange={(e) => setLocal(e.target.value)}
          disabled={disabled}
          aria-label={`Date for ${field}`}
        />
        <button
          type="button"
          className="btn btn-sm btn-primary"
          disabled={disabled || !local}
          onClick={() => {
            if (local) onPick(local);
          }}
        >
          Use this date
        </button>
        {nullable && (
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            disabled={disabled}
            onClick={() => onPick(null)}
          >
            {nullButtonLabel(field)}
          </button>
        )}
      </div>
    </div>
  );
}

function Bubble({ msg, onOptionClick, onDatePick, controlsDisabled }) {
  const isAI = msg.role === 'assistant';
  return (
    <div style={{
      display: 'flex', justifyContent: isAI ? 'flex-start' : 'flex-end',
      marginBottom: 14, animation: 'bubbleIn .2s ease',
    }}>
      {isAI && (
        <div style={{
          width: 32, height: 32, borderRadius: '50%', background: 'linear-gradient(135deg,#1a56db,#6366f1)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          marginRight: 10, flexShrink: 0, marginTop: 2,
        }}>
          <i className="bi bi-stars" style={{ color: '#fff', fontSize: 14 }} aria-hidden="true" />
        </div>
      )}
      <div style={{ maxWidth: '78%' }}>
        <div style={{
          padding: '12px 16px', borderRadius: isAI ? '4px 16px 16px 16px' : '16px 4px 16px 16px',
          background: isAI ? '#fff' : 'linear-gradient(135deg,#1a56db,#4f46e5)',
          color: isAI ? '#111827' : '#fff',
          fontSize: 14, lineHeight: 1.55,
          boxShadow: '0 1px 4px rgba(0,0,0,.08)',
          border: isAI ? '1px solid #e5e7eb' : 'none',
          whiteSpace: 'pre-wrap',
        }}>
          {msg.content}
        </div>

        {/* Option chips — only on the most recent AI message */}
        {isAI && msg.options?.length > 0 && onOptionClick && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
            {msg.options.map((opt, optIdx) => (
              <button
                key={`opt-${optIdx}-${String(opt).slice(0, 48)}`}
                type="button"
                onClick={() => onOptionClick(opt)}
                style={{
                  padding: '5px 14px', borderRadius: 20, fontSize: 13,
                  border: '1.5px solid #1a56db', background: '#eff6ff',
                  color: '#1a56db', fontWeight: 600, cursor: 'pointer',
                  transition: 'all .12s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#1a56db'; e.currentTarget.style.color = '#fff'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = '#eff6ff'; e.currentTarget.style.color = '#1a56db'; }}
              >
                {opt}
              </button>
            ))}
          </div>
        )}

        {isAI && msg.inputHint?.type === 'date' && onDatePick && (
          <DateInputBlock inputHint={msg.inputHint} onPick={onDatePick} disabled={controlsDisabled} />
        )}

        <span style={{ fontSize: 10, color: '#9ca3af', display: 'block', marginTop: 3, textAlign: isAI ? 'left' : 'right' }}>
          {formatMessageTime(msg.ts)}
        </span>
      </div>
    </div>
  );
}

// ─── Session picker ───────────────────────────────────────────────────────────

function SessionPicker({ sessions, onSelect, onNew, onClose }) {
  return (
    <div style={{
      position: 'absolute', top: 48, left: 0, right: 0, zIndex: 10,
      background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12,
      boxShadow: '0 8px 24px rgba(0,0,0,.12)', overflow: 'hidden',
    }}>
      <div style={{ padding: '12px 16px', borderBottom: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>Resume a conversation</span>
        <button type="button" onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}>
          <i className="bi bi-x-lg" aria-hidden="true" />
        </button>
      </div>
      <div style={{ maxHeight: 260, overflowY: 'auto' }}>
        {sessions.length === 0 && (
          <p style={{ padding: '16px', fontSize: 13, color: '#9ca3af', textAlign: 'center' }}>No previous sessions</p>
        )}
        {sessions.map((s) => {
          const draft = s.program_draft || {};
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => onSelect(s.id)}
              style={{
                display: 'flex', flexDirection: 'column', width: '100%', textAlign: 'left',
                padding: '12px 16px', background: 'none', border: 'none', cursor: 'pointer',
                borderBottom: '1px solid #f9fafb',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#f9fafb'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
            >
              <span style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>
                {draft.name || 'Unnamed program'}
              </span>
              <span style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>
                <i className="bi bi-clock me-1" aria-hidden="true" />
                {new Date(s.updated_at).toLocaleString()}
              </span>
            </button>
          );
        })}
      </div>
      <div style={{ padding: '10px 16px', borderTop: '1px solid #f3f4f6' }}>
        <button type="button" className="btn btn-sm btn-outline-primary w-100" onClick={onNew}>
          <i className="bi bi-plus-lg me-1" aria-hidden="true" />Start a new conversation
        </button>
      </div>
    </div>
  );
}

// ─── Draft review panel ───────────────────────────────────────────────────────

function DraftPanel({ draft, onConfirm, onKeepChatting }) {
  const fields = [
    ['Program name', draft.name],
    ['Description', draft.description],
    ['Active', draft.is_active ? 'Yes' : 'Draft'],
    ['Start date', draft.start_date],
    ['End date', draft.end_date || 'No end date'],
    ['Dollar limit / period', draft.amount_limit != null ? `$${draft.amount_limit}` : 'No limit'],
    ['Qty limit / period', draft.qty_limit != null ? `${draft.qty_limit} items` : 'No limit'],
    ['Reset type', { none: 'Never', days: `Every ${draft.period_interval_days ?? '?'} days`, hire_date: 'Per hire date' }[draft.period_type || 'none']],
  ];
  return (
    <div style={{
      margin: '12px 0', background: '#f0fdf4', border: '1.5px solid #86efac',
      borderRadius: 14, padding: '16px 20px',
    }}>
      <p style={{ fontSize: 13, fontWeight: 700, color: '#15803d', margin: '0 0 12px', display: 'flex', alignItems: 'center', gap: 6 }}>
        <i className="bi bi-check-circle-fill" aria-hidden="true" />
        Program is ready to create!
      </p>
      {fields.map(([label, val]) => val != null && val !== '' && (
        <div key={label} style={{ display: 'flex', gap: 10, marginBottom: 4 }}>
          <span style={{ fontSize: 12, color: '#6b7280', width: 150, flexShrink: 0 }}>{label}</span>
          <span style={{ fontSize: 12, fontWeight: 600, color: '#111827' }}>{String(val)}</span>
        </div>
      ))}
      <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
        <button type="button" className="btn btn-sm btn-success" style={{ fontWeight: 600, flex: 1 }} onClick={onConfirm}>
          <i className="bi bi-plus-lg me-1" aria-hidden="true" />Create this program
        </button>
        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onKeepChatting}>
          <i className="bi bi-chat-dots me-1" aria-hidden="true" />Keep editing
        </button>
      </div>
    </div>
  );
}

// ─── Product list sidebar ──────────────────────────────────────────────────────

function ListSidebar({ productLists, scoping }) {
  const [open, setOpen] = useState(true);
  if (!productLists?.length) return null;
  return (
    <div style={{
      width: open ? 240 : 36, flexShrink: 0, borderLeft: '1px solid #e5e7eb',
      background: '#f9fafb', display: 'flex', flexDirection: 'column', transition: 'width .2s',
      overflow: 'hidden',
    }}>
      {/* Toggle */}
      <button type="button" onClick={() => setOpen(v => !v)} style={{
        display: 'flex', alignItems: 'center', gap: 6, padding: '10px 10px',
        background: 'none', border: 'none', cursor: 'pointer', borderBottom: '1px solid #e5e7eb',
        color: '#374151', fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap',
      }}>
        <i className={`bi bi-layout-sidebar-reverse${open ? '-reverse' : ''}`} />
        {open && 'Available Lists'}
      </button>

      {open && (
        <div style={{ flex: 1, overflowY: 'auto', padding: '10px' }}>
          {/* Scoping reminder */}
          {(scoping?.customer_csv || scoping?.ship_to_csv) && (
            <div style={{ background: '#e0f2fe', border: '1px solid #bae6fd', borderRadius: 8, padding: '8px 10px', marginBottom: 10, fontSize: 11 }}>
              <div style={{ fontWeight: 700, color: '#0369a1', marginBottom: 2 }}>
                <i className="bi bi-building me-1" />Scoped to
              </div>
              {scoping.customer_csv && <div style={{ color: '#0284c7' }}>Customer: {scoping.customer_csv}</div>}
              {scoping.ship_to_csv && <div style={{ color: '#0284c7' }}>Site: {scoping.ship_to_csv}</div>}
            </div>
          )}

          <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 8 }}>
            Product Lists ({productLists.length})
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {productLists.map(pl => (
              <div key={pl.id} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 10px' }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#111827', marginBottom: 1 }}>{pl.list_name}</div>
                <div style={{ fontSize: 11, color: '#9ca3af' }}>
                  <i className="bi bi-box-seam me-1" />{pl.item_count || 0} items
                </div>
              </div>
            ))}
          </div>

          <div style={{ marginTop: 12, padding: '8px 10px', background: '#fffbeb', border: '1px solid #fbbf24', borderRadius: 8, fontSize: 11, color: '#92400e' }}>
            <i className="bi bi-lightbulb me-1" />
            Mention list names to the AI when setting up your program, e.g. <em>"use the Gloves list with limit 1"</em>.
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main AI creator ──────────────────────────────────────────────────────────

export default function AIProgramCreator({ onFinish, onCancel, userId = '', productLists = [], scoping = {} }) {
  const [messages, setMessages] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [diagnostics, setDiagnostics] = useState(null);
  const [diagnosticsLoading, setDiagnosticsLoading] = useState(false);
  const [draft, setDraft] = useState(null);
  const [showSessions, setShowSessions] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [error, setError] = useState('');
  const bottomRef = useRef(null);
  const inputRef = useRef(null);
  const hasStartedRef = useRef(false);

  const scrollToBottom = useCallback(() => {
    setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
  }, []);

  // Start fresh session on mount
  useEffect(() => {
    if (hasStartedRef.current) return;
    hasStartedRef.current = true;
    startFresh();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const startFresh = async () => {
    setSending(true);
    setError('');
    try {
      const res = await aiStartSession(userId);
      setSessionId(res.session_id);
      const welcomeMsg = {
        role: 'assistant',
        content: res.message,
        options: res.options || [],
        inputHint: res.input_hint || null,
        ts: new Date().toISOString(),
      };
      setMessages([welcomeMsg]);
      if (res.is_complete && res.program_draft?.name) setDraft(res.program_draft);
      scrollToBottom();
    } catch (e) {
      setError(e.message || 'Failed to start AI session.');
    } finally {
      setSending(false);
    }
  };

  const loadSessions = async () => {
    setLoadingSessions(true);
    try {
      const res = await listAiSessions({ userId, status: 'in_progress' });
      setSessions(res?.items || []);
    } catch (e) {
      setSessions([]);
    } finally {
      setLoadingSessions(false);
    }
  };

  const resumeSession = async (sid) => {
    setShowSessions(false);
    setSending(true);
    setError('');
    try {
      const session = await getAiSession(sid);
      const msgs = (session.messages || []).map((m) => ({
        role: m.role,
        content: m.content,
        options: m.options || [],
        inputHint: m.input_hint ?? m.inputHint ?? null,
        ts: m.ts,
      }));
      setSessionId(sid);
      setMessages(msgs);
      if (session.program_draft?.name) setDraft(session.program_draft);
      scrollToBottom();
    } catch (e) {
      setError(e.message || 'Failed to load session');
    } finally {
      setSending(false);
    }
  };

  const sendMessage = async (override) => {
    let msg;
    if (override === null) {
      msg = 'null';
    } else if (override !== undefined) {
      msg = String(override).trim();
    } else {
      msg = (input || '').trim();
    }
    if (!msg || sending) return;
    setInput('');
    // Optimistic user bubble
    const userMsg = { role: 'user', content: msg, ts: new Date().toISOString() };
    setMessages((prev) => [...prev, userMsg]);
    setSending(true);
    setError('');
    scrollToBottom();
    try {
      const res = await aiChatMessage({ sessionId, message: msg, userId });
      const aiMsg = {
        role: 'assistant',
        content: res.message,
        options: res.options || [],
        inputHint: res.input_hint || null,
        ts: new Date().toISOString(),
      };
      setMessages((prev) => {
        const updated = [...prev, aiMsg];
        return updated.map((m, i) =>
          (m.role === 'assistant' && i < updated.length - 1
            ? { ...m, options: [], inputHint: null }
            : m),
        );
      });
      if (res.session_id && !sessionId) setSessionId(res.session_id);
      if (res.is_complete && res.program_draft?.name) setDraft(res.program_draft);
      scrollToBottom();
    } catch (e) {
      setError(e.message || 'Failed to send message');
      setMessages((prev) => prev.slice(0, -1)); // remove optimistic user msg
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const lastMsgIndex = messages.length - 1;

  return (
    <div style={{ display: 'flex', height: '100%', position: 'relative' }}>
      {/* Main chat column */}
      <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '14px 20px', borderBottom: '1px solid #e5e7eb', flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'linear-gradient(135deg,#1a56db,#6366f1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <i className="bi bi-stars" style={{ color: '#fff', fontSize: 16 }} aria-hidden="true" />
          </div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>AI Program Assistant</div>
            <div style={{ fontSize: 11, color: '#6b7280' }}>Powered by OpenAI</div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            style={{ fontSize: 12 }}
            onClick={() => { loadSessions(); setShowSessions((v) => !v); }}
            title="Resume a previous conversation"
          >
            <i className="bi bi-clock-history me-1" aria-hidden="true" />
            {loadingSessions ? '…' : 'History'}
          </button>
          <button type="button" className="btn btn-sm btn-outline-secondary" style={{ fontSize: 12 }} onClick={startFresh} title="Start over">
            <i className="bi bi-arrow-counterclockwise" aria-hidden="true" />
          </button>
        </div>
      </div>

      {/* Session picker dropdown */}
      {showSessions && (
        <div style={{ position: 'relative', flexShrink: 0 }}>
          <SessionPicker
            sessions={sessions}
            onSelect={resumeSession}
            onNew={() => { setShowSessions(false); startFresh(); }}
            onClose={() => setShowSessions(false)}
          />
        </div>
      )}

      {/* Error / setup banner */}
      {error && (
        isOpenAIKeyMissingError(error) ? (
          <div
            role="alert"
            style={{
              background: '#fffbeb',
              borderBottom: '1px solid #fbbf24',
              padding: '12px 20px',
              fontSize: 13,
              color: '#92400e',
              display: 'flex',
              alignItems: 'flex-start',
              gap: 10,
              flexShrink: 0,
            }}
          >
            <i className="bi bi-key-fill" style={{ fontSize: 18, marginTop: 1 }} aria-hidden="true" />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 700, marginBottom: 2 }}>OpenAI API key not configured</div>
              <div style={{ fontSize: 12, lineHeight: 1.55 }}>
                The AI Program Assistant uses the <code>openaiKey</code> global setting from the
                Customer Identity Module. Open <strong>Customer Identity → Settings</strong>, switch
                to the <em>Manage values</em> view, choose the <strong>Global</strong> scope, and
                paste your key into <code>openaiKey</code>.
              </div>
              <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                <Link
                  to={CI_SETTINGS_PATH}
                  className="btn btn-sm btn-warning"
                  style={{ fontWeight: 600 }}
                >
                  <i className="bi bi-gear-fill me-1" aria-hidden="true" />
                  Open Customer Identity Settings
                </Link>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => { setError(''); startFresh(); }}
                >
                  <i className="bi bi-arrow-clockwise me-1" aria-hidden="true" />
                  Retry
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  disabled={diagnosticsLoading}
                  onClick={async () => {
                    setDiagnosticsLoading(true);
                    try {
                      const result = await aiDiagnostics();
                      setDiagnostics(result);
                    } catch (e) {
                      setDiagnostics({ error: e.message || 'Failed to run diagnostic' });
                    } finally {
                      setDiagnosticsLoading(false);
                    }
                  }}
                >
                  <i className={`bi ${diagnosticsLoading ? 'bi-hourglass-split' : 'bi-clipboard-data'} me-1`} aria-hidden="true" />
                  {diagnosticsLoading ? 'Checking…' : 'Run diagnostic'}
                </button>
              </div>
              {diagnostics && (
                <DiagnosticsPanel data={diagnostics} onClose={() => setDiagnostics(null)} />
              )}
            </div>
            <button
              type="button"
              onClick={() => setError('')}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#92400e', flexShrink: 0 }}
              aria-label="Dismiss"
            >
              <i className="bi bi-x-lg" aria-hidden="true" />
            </button>
          </div>
        ) : (
          <div style={{ background: '#fef2f2', borderBottom: '1px solid #fca5a5', padding: '8px 20px', fontSize: 12, color: '#dc2626', display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
            <i className="bi bi-exclamation-triangle-fill" aria-hidden="true" />
            {error}
            <button type="button" onClick={() => setError('')} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626' }}>
              <i className="bi bi-x-lg" aria-hidden="true" />
            </button>
          </div>
        )
      )}

      {/* Chat messages */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px', background: '#f9fafb' }}>
        {messages.map((msg, i) => (
          <Bubble
            key={i}
            msg={msg}
            onOptionClick={i === lastMsgIndex && msg.role === 'assistant' ? (opt) => sendMessage(opt) : null}
            onDatePick={
              i === lastMsgIndex && msg.role === 'assistant' && msg.inputHint?.type === 'date'
                ? (v) => sendMessage(v === null ? null : v)
                : null
            }
            controlsDisabled={sending}
          />
        ))}

        {/* Draft confirmation panel */}
        {draft && (
          <DraftPanel
            draft={draft}
            onConfirm={() => onFinish(draft)}
            onKeepChatting={() => setDraft(null)}
          />
        )}

        {/* Typing indicator */}
        {sending && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'linear-gradient(135deg,#1a56db,#6366f1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <i className="bi bi-stars" style={{ color: '#fff', fontSize: 14 }} aria-hidden="true" />
            </div>
            <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '4px 16px 16px 16px', padding: '10px 16px' }}>
              <span className="typing-dots">
                <span /><span /><span />
              </span>
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Input area */}
      <div style={{ padding: '12px 16px', borderTop: '1px solid #e5e7eb', background: '#fff', flexShrink: 0 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
          <textarea
            ref={inputRef}
            rows={1}
            className="form-control"
            placeholder="Type your answer… (Enter to send, Shift+Enter for new line)"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={sending}
            style={{ resize: 'none', borderRadius: 10, fontSize: 14, maxHeight: 120, overflow: 'auto' }}
          />
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => sendMessage()}
            disabled={!input.trim() || sending}
            style={{ borderRadius: 10, padding: '8px 14px', flexShrink: 0 }}
          >
            <i className={`bi ${sending ? 'bi-hourglass-split' : 'bi-send-fill'}`} aria-hidden="true" />
          </button>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
          <button type="button" className="btn btn-link btn-sm text-secondary p-0" style={{ fontSize: 12, textDecoration: 'none' }} onClick={onCancel}>
            <i className="bi bi-arrow-left me-1" aria-hidden="true" />Back to Classic Form
          </button>
          <span style={{ fontSize: 11, color: '#d1d5db' }}>
            {sessionId ? `Session #${sessionId}` : 'No session'}
          </span>
        </div>
      </div>

      <style>{`
        @keyframes bubbleIn { from{opacity:0;transform:translateY(6px)} to{opacity:1;transform:none} }
        .typing-dots { display:flex; gap:4px; align-items:center; height:18px; }
        .typing-dots span { width:7px; height:7px; border-radius:50%; background:#d1d5db; animation:bounce 1.2s infinite; }
        .typing-dots span:nth-child(2){animation-delay:.2s}
        .typing-dots span:nth-child(3){animation-delay:.4s}
        @keyframes bounce { 0%,60%,100%{transform:translateY(0)} 30%{transform:translateY(-6px)} }
      `}</style>
      </div>{/* end main chat column */}

      {/* Sidebar: product lists + scoping context */}
      <ListSidebar productLists={productLists} scoping={scoping} />
    </div>
  );
}
